/**
 * @author Filipe Caixeta / http://filipecaixeta.com.br
 * @author Mugen87 / https://github.com/Mugen87
 *
 * Description: A THREE loader for PCD ascii and binary files.
 */

import {
    BufferGeometry,
    FileLoader,
    Float32BufferAttribute,
    Loader,
    LoaderUtils,
    Points,
    PointsMaterial
} from 'three';

var PCDLoader = function ( manager ) {

    Loader.call( this, manager );

    this.littleEndian = true;

};


PCDLoader.prototype = Object.assign( Object.create( Loader.prototype ), {

    constructor: PCDLoader,

    load: function ( url, onLoad, onProgress, onError ) {

        var scope = this;

        var loader = new FileLoader( scope.manager );
        loader.setPath( scope.path );
        loader.setResponseType( 'arraybuffer' );
        loader.load( url, function ( data ) {

            try {

                onLoad( scope.parse( data, url ) );

            } catch ( e ) {

                if ( onError ) {

                    onError( e );

                } else {

                    throw e;

                }

            }

        }, onProgress, onError );

    },

    parse: function ( data, url ) {

        function parseHeader( data ) {

            var PCDheader = {};
            var result1 = data.search( /[\r\n]DATA\s(\S*)\s/i );
            var result2 = /[\r\n]DATA\s(\S*)\s/i.exec( data.substr( result1 - 1 ) );

            PCDheader.data = result2[ 1 ];
            PCDheader.headerLen = result2[ 0 ].length + result1;
            PCDheader.str = data.substr( 0, PCDheader.headerLen );

            // remove comments

            PCDheader.str = PCDheader.str.replace( /\#.*/gi, '' );

            // parse

            PCDheader.version = /VERSION (.*)/i.exec( PCDheader.str );
            PCDheader.fields = /FIELDS (.*)/i.exec( PCDheader.str );
            PCDheader.size = /SIZE (.*)/i.exec( PCDheader.str );
            PCDheader.type = /TYPE (.*)/i.exec( PCDheader.str );
            PCDheader.count = /COUNT (.*)/i.exec( PCDheader.str );
            PCDheader.width = /WIDTH (.*)/i.exec( PCDheader.str );
            PCDheader.height = /HEIGHT (.*)/i.exec( PCDheader.str );
            PCDheader.viewpoint = /VIEWPOINT (.*)/i.exec( PCDheader.str );
            PCDheader.points = /POINTS (.*)/i.exec( PCDheader.str );

            // evaluate

            if ( PCDheader.version !== null )
                PCDheader.version = parseFloat( PCDheader.version[ 1 ] );

            if ( PCDheader.fields !== null )
                PCDheader.fields = PCDheader.fields[ 1 ].split( ' ' );

            if ( PCDheader.type !== null )
                PCDheader.type = PCDheader.type[ 1 ].split( ' ' );

            if ( PCDheader.width !== null )
                PCDheader.width = parseInt( PCDheader.width[ 1 ] );

            if ( PCDheader.height !== null )
                PCDheader.height = parseInt( PCDheader.height[ 1 ] );

            if ( PCDheader.viewpoint !== null )
                PCDheader.viewpoint = PCDheader.viewpoint[ 1 ];

            if ( PCDheader.points !== null )
                PCDheader.points = parseInt( PCDheader.points[ 1 ], 10 );

            if ( PCDheader.points === null )
                PCDheader.points = PCDheader.width * PCDheader.height;

            if ( PCDheader.size !== null ) {

                PCDheader.size = PCDheader.size[ 1 ].split( ' ' ).map( function ( x ) {

                    return parseInt( x, 10 );

                } );

            }

            if ( PCDheader.count !== null ) {

                PCDheader.count = PCDheader.count[ 1 ].split( ' ' ).map( function ( x ) {

                    return parseInt( x, 10 );

                } );

            }

            PCDheader.offset = {};

            var sizeSum = 0;

            for ( var i = 0, l = PCDheader.fields.length; i < l; i ++ ) {

                PCDheader.offset[ PCDheader.fields[ i ] ] = sizeSum;
                sizeSum += PCDheader.size[ i ];

            }

            // for binary only

            PCDheader.rowSize = sizeSum;

            return PCDheader;

        }

        var textData = LoaderUtils.decodeText( new Uint8Array( data ) );

        // parse header (always ascii format)

        var PCDheader = parseHeader( textData );

        // parse data

        var position = [];
        var color = [];

        // binary

        if ( PCDheader.data === 'binary' ) {

            var dataview = new DataView( data, PCDheader.headerLen );
            var offset = PCDheader.offset;

            for ( var i = 0, row = 0; i < PCDheader.points; i ++, row += PCDheader.rowSize ) {

                if ( offset.x !== undefined ) {

                    position.push( dataview.getFloat32( row + offset.x, this.littleEndian ) );
                    position.push( dataview.getFloat32( row + offset.y, this.littleEndian ) );
                    position.push( dataview.getFloat32( row + offset.z, this.littleEndian ) );

                }

                if (offset.size !== undefined) {
                    const size = dataview.getUint8( row + offset.size, this.littleEndian );
                    if (size < 2) {
                        color.push(66.0 / 255.0, 215.0 / 255.0, 245.0 / 255.0)
                    } else {
                        color.push(5.0 / 255.0, 91.0 / 255.0, 171.0 / 255.0)
                    }
                }

            }

        }

        // build geometry

        var geometry = new BufferGeometry();

        if ( position.length > 0 ) geometry.setAttribute( 'position', new Float32BufferAttribute( position, 3 ) );
        if ( color.length > 0 ) geometry.setAttribute( 'color', new Float32BufferAttribute( color, 3 ) );

        geometry.computeBoundingSphere();

        // build material

        var material = new PointsMaterial( { size: 0.005 } );

        if ( color.length > 0 ) {

            material.vertexColors = true;

        } else {

            material.color.setHex( 0x42D7F5 );

        }

        // build point cloud

        var mesh = new Points( geometry, material );
        var name = url.split( '' ).reverse().join( '' );
        name = /([^\/]*)/.exec( name );
        name = name[ 1 ].split( '' ).reverse().join( '' );
        mesh.name = name;

        return mesh;

    }

} );

export { PCDLoader };
